import { useCallback, useState } from "react";
import {
  ITEM_STATUSES,
  STATUS_LABELS,
  type Client,
  type ItemSummary,
  type OpenDeployment,
  type StockRow,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { runBulk, type BulkAction, type BulkFailure } from "../lib/bulkItems.js";
import { useMover } from "../lib/useMover.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { Dialog } from "./Dialog.js";
import { Icon, type IconName } from "./Icon.js";
import { MoveDialog } from "./MoveDialog.js";
import { Notice } from "./Notice.js";
import { RetirePanel, TransferPanel } from "./StockForms.js";
import { useToast } from "./Toast.js";

/** What is open over the bar, if anything. The stock ones carry what they act on. */
type Open =
  | { kind: "move" | "category" | "status" | "tag" | "delete" }
  | { kind: "retire"; rows: StockRow[]; left: number }
  | { kind: "transfer"; lines: OpenDeployment[]; left: number };

/** Labels opens no dialog: it goes to the Labels screen with the selection. */
type Kind = Open["kind"] | "labels";

const DONE: Record<BulkAction["kind"], string> = {
  location: "Moved",
  category: "Category set on",
  status: "Status set on",
  tag: "Tagged",
  delete: "Deleted",
};

/**
 * What can be done to the items selected on the Items or shelf screen: file
 * them, sort them, tag them, change their status, print labels for them, or
 * delete them -- and in a stockroom, retire them or move them to another client.
 *
 * It is there from the moment Select is pressed, so "Select all" is somewhere
 * to find before anything is ticked. Every action is its own button across the
 * width of the bar, and each opens a dialog that says what it will do to how
 * many. Tapping a choice in the dialog (a place, a category, a status, a tag)
 * is the change: the dialog's title has already said what will happen, and a
 * second Apply would be a tap on every change to guard against the rare wrong
 * one. Moving's toast offers Undo. Deleting asks in words that say how many.
 *
 * Retire and Move to client are the Stock screen's own panels, fed from the
 * stock overview at the moment the button is pressed: a selection on the Items
 * screen can include things that are not stock, or not out at anyone, and
 * those are left out and counted rather than refused as a whole.
 *
 * A failure is listed by title and the failed items stay ticked, so doing the
 * same thing again retries only those.
 */
export function BulkBar({
  items,
  total,
  picked,
  taxonomy,
  vocabulary,
  stock = false,
  onSelectAll,
  onClear,
  onDone,
  onLabels,
  here,
}: {
  /** Print labels or cards for the selection. Left out, the button is not offered. */
  onLabels?: () => void;
  /** The location these items are all in, when the screen is one location. */
  here?: string | null;
  /** What is loaded, for naming a failure. */
  items: ItemSummary[];
  /** How many match, loaded or not: what "Select all" will tick. */
  total: number;
  picked: Set<string>;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  /** A stockroom: offers Retire and Move to client. */
  stock?: boolean;
  /** Ticks every match, reading the pages not loaded yet. */
  onSelectAll: () => Promise<void>;
  /** Leave with these ids still ticked: the ones that failed. */
  onClear: (keep?: string[]) => void;
  /** Something changed, so the list should be read again. */
  onDone: () => void;
}) {
  const [open, setOpen] = useState<Open | null>(null);
  const [tag, setTag] = useState("");
  const [working, setWorking] = useState<number | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [failed, setFailed] = useState<BulkFailure[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const toast = useToast();

  const whereIs = useCallback(
    (id: string) => {
      const item = items.find((one) => one.id === id);
      return item ? item.locationId : undefined;
    },
    [items],
  );
  const mover = useMover({ taxonomy, whereIs, onDone });

  const count = picked.size;
  const everything = count > 0 && count >= total;
  const idle = working === null && mover.working === null && !selecting && !loadingStock;
  const close = useCallback(() => setOpen(null), []);

  async function apply(action: BulkAction) {
    if (count === 0) return;
    setOpen(null);
    const ids = [...picked];
    setWorking(0);
    setFailed([]);
    setProblem(null);
    const failures = await runBulk(ids, action, setWorking);
    setWorking(null);
    setFailed(failures);
    onClear(failures.map((failure) => failure.id));
    onDone();
    const changed = ids.length - failures.length;
    if (changed > 0) toast(`${DONE[action.kind]} ${plural(changed, "item")}.`);
    if (action.kind === "tag") {
      setTag("");
      void vocabulary.reloadTags();
    }
    // Category counts moved, and a deleted item took its location's count with it.
    if (action.kind !== "status" && action.kind !== "tag") void taxonomy.reload();
  }

  async function move(to: string | null) {
    setOpen(null);
    const ids = [...picked];
    setFailed([]);
    setProblem(null);
    const failures = await mover.move(ids, to);
    setFailed(failures);
    onClear(failures.map((failure) => failure.id));
  }

  async function selectAll() {
    setSelecting(true);
    try {
      await onSelectAll();
    } finally {
      setSelecting(false);
    }
  }

  /** Reads the stockroom as it is now, and opens Retire or Move to client on the part of the selection it applies to. */
  async function openStock(kind: "retire" | "transfer") {
    setLoadingStock(true);
    setFailed([]);
    setProblem(null);
    try {
      const [overview, list] = await Promise.all([
        api.stock.overview(),
        kind === "transfer" ? api.clients.list() : Promise.resolve(null),
      ]);
      if (kind === "retire") {
        // The item screen's rule: a serialized item can go from anywhere, bulk units only from the shelf.
        const rows = overview.items.filter(
          (row) =>
            picked.has(row.itemId) &&
            row.state !== "retired" &&
            (row.tracking === "serialized" || row.onHand > 0),
        );
        if (rows.length === 0) {
          setProblem(
            count === 1
              ? "That item cannot be retired: it is not stock, it is already retired, or its units are all out at a client. Return them first."
              : "None of these can be retired: they are not stock, they are already retired, or their units are all out at a client. Return them first.",
          );
          return;
        }
        setOpen({ kind, rows, left: count - rows.length });
      } else {
        const lines = overview.open.filter((line) => picked.has(line.itemId));
        if (lines.length === 0) {
          setProblem(
            count === 1
              ? "That item is not out at a client. Deploy it from the Stock screen first."
              : "None of these are out at a client. Deploy them from the Stock screen first.",
          );
          return;
        }
        setClients(list?.clients ?? []);
        const moving = new Set(lines.map((line) => line.itemId));
        setOpen({ kind, lines, left: count - moving.size });
      }
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "Could not read the stockroom.");
    } finally {
      setLoadingStock(false);
    }
  }

  function stockDone() {
    setOpen(null);
    onClear();
    onDone();
  }

  const titleOf = (id: string) => items.find((item) => item.id === id)?.title || "Untitled item";
  const many = plural(count, "item");

  const actions: { kind: Kind; icon: IconName; label: string; danger?: boolean }[] = [
    { kind: "move", icon: "folder", label: "Move" },
    { kind: "category", icon: "grid", label: "Category" },
    { kind: "status", icon: "bag", label: "Status" },
    { kind: "tag", icon: "tag", label: "Tag" },
    ...(stock
      ? ([
          { kind: "transfer", icon: "external", label: "Client" },
          { kind: "retire", icon: "archive", label: "Retire" },
        ] as const)
      : []),
    ...(onLabels ? ([{ kind: "labels", icon: "qr", label: "Labels" }] as const) : []),
    { kind: "delete", icon: "trash", label: "Delete", danger: true },
  ];

  function choose(kind: Kind) {
    setProblem(null);
    if (kind === "labels") onLabels?.();
    else if (kind === "retire" || kind === "transfer") void openStock(kind);
    else setOpen({ kind });
  }

  const leftOut = (left: number, why: string) =>
    left > 0 && (
      <Notice tone="warn">{`${plural(left, "selected item")} ${why}, so ${left === 1 ? "it is" : "they are"} left out.`}</Notice>
    );

  return (
    <>
      {failed.length > 0 && (
        <Notice tone="error">
          {`${plural(failed.length, "item")} did not change: ${failed
            .map((failure) => `${titleOf(failure.id)} (${failure.message})`)
            .join("; ")}`}
        </Notice>
      )}
      {problem && <Notice tone="error">{problem}</Notice>}
      <div className="action-bar action-bar-float bulk-bar">
        <div className="bulk-head">
          <p className="bulk-count" aria-live="polite">
            {working !== null || mover.working !== null
              ? `Working… ${working ?? mover.working} of ${count}`
              : selecting
                ? "Selecting all…"
                : loadingStock
                  ? "Reading the stockroom…"
                  : count === 0
                    ? "Tap items to select them"
                    : `${count} of ${total} selected`}
          </p>
          <button
            type="button"
            className="button button-quiet button-small"
            disabled={!idle}
            onClick={() => (everything ? onClear() : void selectAll())}
          >
            {everything ? "Select none" : `Select all ${total}`}
          </button>
        </div>
        <div className="bulk-actions" role="group" aria-label="What to do with the selected items">
          {actions.map((action) => (
            <button
              key={action.kind}
              type="button"
              className={action.danger ? "bulk-action bulk-action-danger" : "bulk-action"}
              disabled={!idle || count === 0}
              onClick={() => choose(action.kind)}
            >
              <Icon name={action.icon} size={20} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {open?.kind === "move" && (
        <MoveDialog
          title={`Move ${many} to…`}
          taxonomy={taxonomy}
          {...(here !== undefined ? { here } : {})}
          onChoose={(to) => void move(to)}
          onClose={close}
        />
      )}

      {open?.kind === "category" && (
        <Dialog title={`Set the category of ${many}`} onClose={close}>
          <div className="chooser-recent">
            {taxonomy.categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className="chooser-quick"
                onClick={() => void apply({ kind: "category", categoryId: category.id })}
              >
                <Icon name="grid" size={14} />
                <span>{category.name}</span>
              </button>
            ))}
            <button
              type="button"
              className="chooser-quick"
              onClick={() => void apply({ kind: "category", categoryId: null })}
            >
              <Icon name="x" size={14} />
              <span>No category</span>
            </button>
          </div>
        </Dialog>
      )}

      {open?.kind === "status" && (
        <Dialog title={`Set the status of ${many}`} onClose={close}>
          <div className="chooser-recent">
            {ITEM_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                className="chooser-quick"
                onClick={() => void apply({ kind: "status", status })}
              >
                <span>{STATUS_LABELS[status]}</span>
              </button>
            ))}
          </div>
        </Dialog>
      )}

      {open?.kind === "tag" && (
        <Dialog title={`Tag ${many}`} onClose={close}>
          <form
            className="bulk-tag"
            onSubmit={(event) => {
              event.preventDefault();
              if (tag.trim()) void apply({ kind: "tag", name: tag.trim() });
            }}
          >
            <input
              className="input"
              aria-label="A new tag"
              placeholder="Type a tag"
              autoFocus
              value={tag}
              onChange={(event) => setTag(event.target.value)}
            />
            <button type="submit" className="button" disabled={!tag.trim()}>
              Add
            </button>
          </form>
          {vocabulary.tags.length > 0 && (
            <div className="chooser-recent" aria-label="Tags you already use">
              {vocabulary.tags
                .filter((one) => one.name.toLowerCase().includes(tag.trim().toLowerCase()))
                .map((one) => (
                  <button
                    key={one.id}
                    type="button"
                    className="chooser-quick"
                    onClick={() => void apply({ kind: "tag", name: one.name })}
                  >
                    <Icon name="tag" size={14} />
                    <span>{one.name}</span>
                  </button>
                ))}
            </div>
          )}
        </Dialog>
      )}

      {open?.kind === "delete" && (
        <Dialog title={`Delete ${many}?`} onClose={close}>
          <p className="muted">
            {count === 1 ? "It goes" : "They go"} for good, with {count === 1 ? "its" : "their"} photos. This
            cannot be undone.
          </p>
          <div className="panel-foot">
            <button type="button" className="button button-quiet" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="button button-danger"
              onClick={() => void apply({ kind: "delete" })}
            >
              <Icon name="trash" size={16} />
              {`Delete ${count}`}
            </button>
          </div>
        </Dialog>
      )}

      {open?.kind === "retire" && (
        <Dialog wide label="Retire the selected items" onClose={close}>
          {leftOut(
            open.left,
            open.left === 1 ? "is not stock that can be retired" : "are not stock that can be retired",
          )}
          <RetirePanel rows={open.rows} onCancel={close} onDone={stockDone} />
        </Dialog>
      )}

      {open?.kind === "transfer" && (
        <Dialog wide label="Move the selected items to another client" onClose={close}>
          {leftOut(open.left, open.left === 1 ? "is not out at a client" : "are not out at a client")}
          <TransferPanel
            lines={open.lines}
            clients={clients}
            onClients={setClients}
            onCancel={close}
            onDone={stockDone}
          />
        </Dialog>
      )}
    </>
  );
}
